import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { MessageSquareQuote, Pencil, Trash2, Star } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY = { guest_name: "", rating: 5, source: "", date: "", text: "" };

export const AdminReviews = ({ headers }) => {
  const [list, setList] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const inputCls =
    "w-full bg-transparent border-b border-[#C29B62]/40 focus:border-[#1C3829] outline-none py-2.5 text-sm font-light placeholder:text-[#1A1A1A]/30 transition-colors";
  const labelCls = "block text-xs uppercase tracking-[0.2em] text-[#1A1A1A]/50 mb-1";

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/reviews`);
      setList(res.data);
    } catch {
      toast.error("Errore nel caricamento delle recensioni");
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = {
      guest_name: form.guest_name,
      rating: Number(form.rating),
      text: form.text,
      source: form.source || null,
      date: form.date || null,
    };
    try {
      if (editing) {
        await axios.put(`${API}/admin/reviews/${editing}`, payload, { headers: headers() });
        toast.success("Recensione aggiornata");
      } else {
        await axios.post(`${API}/admin/reviews`, payload, { headers: headers() });
        toast.success("Recensione pubblicata sul sito");
      }
      setForm(EMPTY);
      setEditing(null);
      load();
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Errore nel salvataggio");
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (r) => {
    setEditing(r.id);
    setForm({
      guest_name: r.guest_name,
      rating: r.rating,
      source: r.source || "",
      date: r.date || "",
      text: r.text,
    });
  };

  const remove = async (r) => {
    if (!window.confirm(`Eliminare la recensione di "${r.guest_name}"?`)) return;
    try {
      await axios.delete(`${API}/admin/reviews/${r.id}`, { headers: headers() });
      toast.success("Recensione eliminata");
      if (editing === r.id) {
        setEditing(null);
        setForm(EMPTY);
      }
      load();
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Errore nell'eliminazione");
    }
  };

  return (
    <section data-testid="admin-reviews-section" className="border border-[#C29B62]/25 bg-white/60 p-8">
      <div className="flex items-center gap-3">
        <MessageSquareQuote className="w-4 h-4 text-[#C29B62]" />
        <h2 className="text-2xl font-light text-[#1C3829] font-serif-display">Recensioni</h2>
      </div>
      <p className="mt-2 text-xs text-[#1A1A1A]/50">
        Le recensioni vere degli ospiti (copiate da Airbnb, Booking, ecc.) mostrate nella sezione "Recensioni" del sito.
        La più recente appare in grande come recensione principale.
      </p>

      <div className="mt-6 divide-y divide-[#C29B62]/15 border-y border-[#C29B62]/25" data-testid="admin-reviews-list">
        {list.map((r) => (
          <div key={r.id} data-testid={`admin-review-row-${r.id}`} className="py-4 flex flex-wrap items-center justify-between gap-3">
            <div className="min-w-0">
              <div className="flex items-center gap-3">
                <p className="text-base text-[#1A1A1A]">{r.guest_name}</p>
                <span className="flex gap-0.5">
                  {[1, 2, 3, 4, 5].map((s) => (
                    <Star
                      key={s}
                      className={`w-3 h-3 ${s <= r.rating ? "text-[#C29B62]" : "text-[#1A1A1A]/20"}`}
                      fill={s <= r.rating ? "currentColor" : "none"}
                    />
                  ))}
                </span>
              </div>
              <p className="text-xs text-[#1A1A1A]/50">
                {[r.source, r.date].filter(Boolean).join(" · ") || "—"}
              </p>
              <p className="mt-1 text-xs italic text-[#1A1A1A]/40 max-w-xl truncate" title={r.text}>{r.text}</p>
            </div>
            <div className="flex gap-2">
              <button
                type="button"
                data-testid={`admin-review-edit-${r.id}`}
                onClick={() => startEdit(r)}
                title="Modifica"
                className="p-2 border border-[#1C3829]/30 text-[#1C3829] hover:bg-[#1C3829] hover:text-[#F7F5F0] transition-colors"
              >
                <Pencil className="w-4 h-4" />
              </button>
              <button
                type="button"
                data-testid={`admin-review-delete-${r.id}`}
                onClick={() => remove(r)}
                title="Elimina"
                className="p-2 border border-red-800/30 text-red-800 hover:bg-red-800 hover:text-white transition-colors"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          </div>
        ))}
        {list.length === 0 && (
          <p className="py-6 text-sm text-[#1A1A1A]/40" data-testid="admin-reviews-empty">Nessuna recensione: la sezione non viene mostrata sul sito.</p>
        )}
      </div>

      <form onSubmit={submit} className="mt-8 space-y-6" data-testid="admin-review-form">
        <p className="text-xs uppercase tracking-[0.2em] text-[#1C3829]">
          {editing ? `Modifica: ${form.guest_name}` : "Aggiungi una nuova recensione"}
        </p>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <div>
            <label className={labelCls}>Nome ospite</label>
            <input data-testid="review-name-input" required className={inputCls} value={form.guest_name} onChange={set("guest_name")} placeholder="Es. Volker" />
          </div>
          <div>
            <label className={labelCls}>Voto</label>
            <select data-testid="review-rating-select" className={`${inputCls} bg-transparent`} value={form.rating} onChange={set("rating")}>
              {[5, 4, 3, 2, 1].map((v) => (
                <option key={v} value={v}>{v} stelle</option>
              ))}
            </select>
          </div>
          <div>
            <label className={labelCls}>Portale (facoltativo)</label>
            <input data-testid="review-source-input" className={inputCls} value={form.source} onChange={set("source")} placeholder="Es. Airbnb" />
          </div>
          <div>
            <label className={labelCls}>Periodo (facoltativo)</label>
            <input data-testid="review-date-input" className={inputCls} value={form.date} onChange={set("date")} placeholder="Es. Agosto 2026" />
          </div>
        </div>
        <div>
          <label className={labelCls}>Testo della recensione</label>
          <textarea
            data-testid="review-text-input"
            required
            rows={4}
            className={`${inputCls} resize-none`}
            value={form.text}
            onChange={set("text")}
            placeholder="Incolla qui il testo originale della recensione…"
          />
        </div>
        <div className="flex items-center gap-4">
          <button
            data-testid="admin-review-submit"
            type="submit"
            disabled={saving}
            className="bg-[#1C3829] text-[#F7F5F0] text-xs uppercase tracking-[0.25em] px-8 py-3.5 hover:text-[#C29B62] transition-colors disabled:opacity-40"
          >
            {editing ? "Salva modifiche" : "Pubblica recensione"}
          </button>
          {editing && (
            <button
              type="button"
              data-testid="admin-review-cancel-edit"
              onClick={() => { setEditing(null); setForm(EMPTY); }}
              className="text-xs uppercase tracking-[0.2em] text-[#1A1A1A]/50 hover:text-[#1C3829] transition-colors"
            >
              Annulla modifica
            </button>
          )}
        </div>
      </form>
    </section>
  );
};
